import { LiveAnnouncer } from '@angular/cdk/a11y';
import { IconMenuButtonComponent } from 'src/app/shared/components/icon-menu-button/icon-menu-button.component';
import { Component, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort, MatSortHeader } from '@angular/material/sort';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { Router } from '@angular/router';
import { PhoneModel } from 'src/app/models/phone.model';
import { SupplierModel } from 'src/app/models/supplier.model';
import { SupplierService } from 'src/app/services/supplier.services';
import { ToastService } from 'src/app/services/toast.service';
import { ImageService } from 'src/app/shared/services/image.service';
import { SharedServices } from 'src/app/shared/services/shared.service';
import { UserService } from 'src/app/shared/services/user.service';
import { SupplierUserAssignComponent } from '../supplier-user-assign/supplier-user-assign.component';
import { WorkingAreaResponseModel } from 'src/app/models/workingArea-response.model';
import { CommonFunctionsConfiguratorService } from 'src/app/shared/services/swal-fire-configurator.service';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatMenu, MatMenuItem } from '@angular/material/menu';
import { SupplierZoneFilterComponent } from '../supplier-zone-filter/supplier-zone-filter.component';
import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';
import { EmptyStateComponent } from 'src/app/shared/ui/empty-state/empty-state.component';

@Component({
    selector: 'app-supplier-list',
    templateUrl: './supplier-list.component.html',
    styleUrls: ['./supplier-list.component.scss'],
    imports: [IconMenuButtonComponent, MatIcon, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, NgClass, MatButton, MatMenu, MatMenuItem, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, SupplierZoneFilterComponent, PageHeaderComponent, EmptyStateComponent]
})
export class SupplierListComponent implements OnInit {
  private readonly _liveAnnouncer = inject(LiveAnnouncer);
  imageSvc = inject(ImageService);
  private readonly supplerSrv = inject(SupplierService);
  usrSvc = inject(UserService);
  private readonly router = inject(Router);
  toastr = inject(ToastService);
  dialog = inject(MatDialog);
  private readonly paginatorService = inject(CommonFunctionsConfiguratorService);
  private readonly sharedService = inject(SharedServices);

  displayedColumns: string[] = [
    'fullName',
    'actionEmail',
    'dni',
    'phone1',
    'score',
    'actionActivateDeactivate',
    'actionMenu',
  ];
  dataSource = new MatTableDataSource<SupplierModel>();

  // static: the suppliers can arrive synchronously (cached GET /providers) inside ngOnInit, before a non-static
  // query is resolved; the table and the paginator are never inside an @if.
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  supplerList: SupplierModel[] = [];
  suppliersCounter: number = 0;
  showFilterModal: boolean = false;
  center!: google.maps.LatLngLiteral;
  filterZone: WorkingAreaResponseModel | undefined;
  /** True after the first response arrives; avoids showing the empty state while loading. */
  loaded = false;

  ngOnInit(): void {
    this.getSuppliers();
  }

  getSuppliers() {
    const suppliers$ = this.filterZone
      ? this.supplerSrv.getSupliersByZone(this.filterZone)
      : this.supplerSrv.getAll();

    suppliers$.subscribe((data: SupplierModel[]) => {
      this.supplerList = data;
      this.loaded = true;
      this.suppliersCounter = data.length;
      const filter = this.dataSource.filter;
      this.dataSource = new MatTableDataSource(this.supplerList);
      // There is no phone1 property: sort the Teléfono column by the first phone shown in it.
      this.dataSource.sortingDataAccessor = (supplier, column) =>
        column === 'phone1'
          ? this.formatPhone(supplier.phones?.[0])
          : (supplier as any)[column];
      this.dataSource.filter = filter; // a zone change keeps the search typed in the box
      this.paginatorService.configurePaginator(
        this.dataSource,
        this.paginator,
        this.sort,
        "Prestadores"
      );
      if (this.filterZone) {
        this.backToList();
      }
    });
  }

  /** Suppliers shown after the search and the zone filter: the header and footer count. */
  get visibleCount(): number {
    return this.dataSource.filteredData.length;
  }

  setFilteredZone(zone: any) {
    if (zone) {
      this.filterZone = zone;
      this.center = {
        lat: zone.latitude,
        lng: zone.longitude,
      };
      this.getSuppliers();
    } else {
      this.filterZone = undefined;
      this.getSuppliers();
    }
  }

  associateSeller(supplier: SupplierModel) {
    this.dialogAction(supplier);
  }

  dialogAction(data: SupplierModel): void {
    this.dialog.open(SupplierUserAssignComponent, {
      // A fixed width capped by the viewport: 30vw was 117px on a phone.
      width: '480px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '95vh',
      panelClass: '.detailClass',
      data: { data },
    });
  }

  onRowClick(element: any) {
    this.goSupplierDetail(element);
  }

  onRowKeydown(event: KeyboardEvent, element: any) {
    if (event.key === 'Enter') {
      this.goSupplierDetail(element);
    }
  }

  goSupplierDetail(supplier: SupplierModel) {
    this.router.navigate(['/supplier'], { queryParams: { id: supplier.id } });
  }

  goSupplierSkills(supplier: SupplierModel) {
    this.router.navigate(['/supplier'], {
      fragment: 'skills',
      queryParams: { id: supplier.id },
    });
  }

  goSupplierAssistant(supplier: SupplierModel) {
    this.router.navigate(['/supplier'], {
      fragment: 'colaborators',
      queryParams: { id: supplier.id },
    });
  }

  goSupplierVehicle(supplier: SupplierModel) {
    this.router.navigate(['/supplier'], {
      fragment: 'vehicles',
      queryParams: { id: supplier.id },
    });
  }

  goWorkingAreas(supplier: SupplierModel) {
    this.router.navigate(['/supplier'], {
      fragment: 'working-area',
      queryParams: { id: supplier.id },
    });
  }

  goSupplierHours(supplier: SupplierModel) {
    this.router.navigate(['/supplier'], {
      fragment: 'working-days',
      queryParams: { id: supplier.id },
    });
  }

  addSupplier() {
    this.router.navigate(['/supplier']);
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  formatPhone(phone: PhoneModel) {
    if (!phone?.number) {
      return '';
    }
    return `${phone.countryCode}-${phone.number}`;
  }

  async activateDeactivate(e: any) {
    // Deactivating takes the contractor out of the app: ask first. Activating needs no confirmation.
    if (e.isActive) {
      const result = await this.paginatorService.SwalFireQuestion(
        'Desactivar prestador',
        `¿Desea desactivar a ${e.fullName}?`,
        'Desactivar',
        'Cancelar'
      );
      if (!result.isConfirmed) {
        return;
      }
    }

    e.isActive = !e.isActive;
    this.supplerSrv.active(e.id, e.isActive).subscribe(
      (data) => { },
      (err) => {
        let msg = e.isActive ? 'activar' : 'desactivar';
        e.isActive = !e.isActive;
        this.toastr.error('Error', 'No se ha podido ' + msg + ' el prestador.');
      }
    );
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  openFilterModal() {
    this.showFilterModal = true;
  }

  @HostListener('window:keydown.escape', ['$event'])
  backToList(event?: KeyboardEvent) {
    this.showFilterModal = false;
  }
}
